import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { ENDPOINTS, resolveAssetUrl } from '../config';

function CountdownTimer({ endDate }) {
    const [timeLeft, setTimeLeft] = useState(getTimeLeft(endDate));

    function getTimeLeft(end) {
        const diff = new Date(end) - new Date();
        if (diff <= 0) return null;
        return {
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((diff / (1000 * 60)) % 60),
            seconds: Math.floor((diff / 1000) % 60),
        };
    }

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(getTimeLeft(endDate));
        }, 1000);
        return () => clearInterval(timer);
    }, [endDate]);

    if (!timeLeft) return <span className="text-white/80 text-sm">This offer has ended</span>;

    return (
        <div className="flex items-center gap-3">
            <span className="text-white/80 text-sm">Ends in:</span>
            <div className="flex gap-1.5">
                {timeLeft.days > 0 && (
                    <div className="bg-card/20 backdrop-blur rounded px-2.5 py-1.5 text-center min-w-[44px]">
                        <div className="text-white text-lg font-bold leading-none">{timeLeft.days}</div>
                        <div className="text-white/70 text-[10px] mt-0.5">DAYS</div>
                    </div>
                )}
                <div className="bg-card/20 backdrop-blur rounded px-2.5 py-1.5 text-center min-w-[44px]">
                    <div className="text-white text-lg font-bold leading-none">{String(timeLeft.hours).padStart(2, '0')}</div>
                    <div className="text-white/70 text-[10px] mt-0.5">HRS</div>
                </div>
                <div className="text-white text-lg font-bold">:</div>
                <div className="bg-card/20 backdrop-blur rounded px-2.5 py-1.5 text-center min-w-[44px]">
                    <div className="text-white text-lg font-bold leading-none">{String(timeLeft.minutes).padStart(2, '0')}</div>
                    <div className="text-white/70 text-[10px] mt-0.5">MIN</div>
                </div>
                <div className="text-white text-lg font-bold">:</div>
                <div className="bg-card/20 backdrop-blur rounded px-2.5 py-1.5 text-center min-w-[44px]">
                    <div className="text-white text-lg font-bold leading-none">{String(timeLeft.seconds).padStart(2, '0')}</div>
                    <div className="text-white/70 text-[10px] mt-0.5">SEC</div>
                </div>
            </div>
        </div>
    );
}

/**
 * Group offer line items by product — one card per product in the grid.
 * Prefers purchasable variants, then lowest display price.
 */
function groupOfferProductsForGrid(rawProducts, offer) {
    const byProductId = new Map();

    for (const p of rawProducts) {
        const productId = String(p._id);
        const variant = p.variant || {};
        const salePrice = variant.price?.sale_price || 0;
        const costPrice = variant.price?.cost_price || salePrice;
        const offerPrice = variant.offerPrice ?? null;
        const displayPrice = offerPrice != null && offerPrice < salePrice ? offerPrice : salePrice;
        const hasVariantOffer = offerPrice != null && offerPrice < salePrice;
        const purchasable =
            typeof variant.purchasable === 'boolean'
                ? variant.purchasable
                : typeof p.inStock === 'boolean'
                  ? p.inStock
                  : !variant.forceOutOfStock;

        const row = { variant, salePrice, costPrice, offerPrice, displayPrice, hasVariantOffer, purchasable };

        if (!byProductId.has(productId)) {
            byProductId.set(productId, {
                _id: p._id,
                name: p.name,
                slug: p.slug,
                brandId: p.brandId,
                categoryIds: p.categoryIds,
                rows: [row],
            });
        } else {
            byProductId.get(productId).rows.push(row);
        }
    }

    return Array.from(byProductId.values()).map((group) => {
        const sorted = [...group.rows].sort((a, b) => {
            // In-stock / purchasable first, then cheapest
            if (a.purchasable !== b.purchasable) return a.purchasable ? -1 : 1;
            return a.displayPrice - b.displayPrice;
        });
        const best = sorted[0];
        const bestVariant = best.variant;
        const primaryImg = bestVariant.variantImage?.find((img) => img.isPrimary)
            || bestVariant.variantImage?.[0];
        const uniquePrices = new Set(group.rows.map((r) => r.displayPrice));
        const hasOfferInGroup = group.rows.some((r) => r.hasVariantOffer);
        const maxCost = Math.max(...group.rows.map((r) => r.costPrice || 0));
        const displayPrice = best.displayPrice;

        return {
            id: group._id,
            _id: group._id,
            name: group.name,
            slug: group.slug,
            variantSlug: bestVariant.slug || null,
            variant: bestVariant.slug
                ? { _id: bestVariant._id, slug: bestVariant.slug }
                : null,
            variantId: bestVariant._id || null,
            defaultVariantId: bestVariant._id || null,
            availableVariantId: bestVariant._id || null,
            image: primaryImg ? resolveAssetUrl(primaryImg.path) : '/placeholder.png',
            lowestPrice: displayPrice,
            displayPrice,
            priceFrom: uniquePrices.size > 1,
            costPrice: maxCost,
            price: displayPrice,
            originalPrice: maxCost,
            offerPricing: hasOfferInGroup
                ? {
                    offerPrice: displayPrice,
                    offer: { _id: offer?._id, name: offer?.name || 'Offer' },
                }
                : null,
            discount: maxCost > displayPrice
                ? Math.round(((maxCost - displayPrice) / maxCost) * 100)
                : 0,
            variantsInOfferCount: group.rows.length,
            inStock: Boolean(best.purchasable),
            forceOutOfStock: !best.purchasable,
            rating: 0,
            reviews: 0,
        };
    });
}

export default function OfferDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [offer, setOffer] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        const fetchOffer = async () => {
            try {
                const res = await fetch(ENDPOINTS.OFFERS.BY_SLUG(slug));
                const data = await res.json();

                if (data.isOk && data.data) {
                    setOffer(data.data);
                    setProducts(groupOfferProductsForGrid(data.data.products || [], data.data));
                } else {
                    setNotFound(true);
                }
            } catch (err) {
                console.error('Error fetching offer:', err);
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };
        fetchOffer();
    }, [slug]);

    if (loading) {
        return (
            <main className="pt-[10px] pb-12">
                <div className="max-w-[1400px] mx-auto px-4">
                    <div className="animate-pulse">
                        <div className="h-64 bg-muted rounded-lg mb-8"></div>
                        <div className="h-8 bg-muted rounded w-1/3 mb-4"></div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                            {[...Array(8)].map((_, i) => (
                                <div key={i} className="bg-card border border-border rounded-lg overflow-hidden">
                                    <div className="w-full h-48 bg-muted"></div>
                                    <div className="p-4 space-y-3">
                                        <div className="h-4 bg-muted rounded w-3/4"></div>
                                        <div className="h-6 bg-muted rounded w-1/3"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    if (notFound) {
        return (
            <main className="pt-[10px] pb-12">
                <div className="max-w-[1400px] mx-auto px-4 text-center py-16">
                    <i className="ri-error-warning-line text-5xl text-muted-foreground/40 mb-4 block"></i>
                    <h2 className="text-2xl font-bold text-foreground mb-2">Offer not found</h2>
                    <p className="text-muted-foreground mb-6">This offer may have expired or been removed.</p>
                    <button
                        onClick={() => navigate('/offers')}
                        className="px-6 py-2 bg-primary text-white rounded-lg hover:brightness-110 transition-all"
                    >
                        View All Offers
                    </button>
                </div>
            </main>
        );
    }

    const bannerUrl = offer?.bannerImage ? resolveAssetUrl(offer.bannerImage) : null;

    return (
        <main className="pt-[10px] pb-12">
            {/* Hero Banner */}
            <div
                className="relative w-full"
                style={{ backgroundColor: offer?.bgColor || '#F05023' }}
            >
                {bannerUrl ? (
                    <img
                        src={bannerUrl}
                        alt={offer?.name}
                        className="w-full h-64 md:h-80 object-cover"
                    />
                ) : (
                    <div className="w-full h-64 md:h-80 bg-gradient-to-r from-primary to-primary/80 flex items-center justify-center">
                        <div className="text-center text-white">
                            <i className="ri-price-tag-3-line text-6xl mb-4 block"></i>
                            <h1 className="text-4xl font-bold mb-2">{offer?.name}</h1>
                            {offer?.discountValue > 0 && (
                                <p className="text-2xl font-semibold">
                                    {offer?.discountType === 'percentage'
                                        ? `Up to ${offer?.discountValue}% OFF`
                                        : `Save KSh ${offer?.discountValue.toLocaleString()}`
                                    }
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Overlay with timer */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                    <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        <div>
                            {bannerUrl && (
                                <h1 className="text-3xl font-bold text-white mb-1">{offer?.name}</h1>
                            )}
                            {offer?.discountValue > 0 && bannerUrl && (
                                <p className="text-white/90 text-lg">
                                    {offer?.discountType === 'percentage'
                                        ? `Up to ${offer?.discountValue}% OFF`
                                        : `Save KSh ${offer?.discountValue.toLocaleString()}`
                                    }
                                </p>
                            )}
                        </div>
                        <CountdownTimer endDate={offer?.endDate} />
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto px-4">
                {/* Breadcrumb */}
                <nav className="py-4 text-sm">
                    <ol className="flex items-center gap-2 text-muted-foreground">
                        <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                        <li><i className="ri-arrow-right-s-line"></i></li>
                        <li><Link to="/offers/" className="hover:text-primary transition-colors">Offers</Link></li>
                        <li><i className="ri-arrow-right-s-line"></i></li>
                        <li className="text-foreground font-medium">{offer?.name}</li>
                    </ol>
                </nav>

                {/* Description */}
                {offer?.description && (
                    <div className="mb-8 p-4 bg-[#FFF3E0] rounded-lg border border-[#FFE0B2]">
                        <p className="text-muted-foreground">{offer.description}</p>
                    </div>
                )}

                {/* Products Header */}
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-foreground">
                        Products in this offer
                    </h2>
                    <p className="text-muted-foreground mt-1">{products.length} product{products.length !== 1 ? 's' : ''}</p>
                </div>

                {/* Products Grid */}
                {products.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {products.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <i className="ri-inbox-line text-4xl text-muted-foreground/40 mb-4 block"></i>
                        <p className="text-muted-foreground">No products available for this offer yet.</p>
                    </div>
                )}
            </div>
        </main>
    );
}
